import { useMemo } from "react";
import styled from "styled-components";

import KpiStrip from "../KpiStrip";
import MemberFunnel from "../MemberFunnel";
import MemberMiniList from "../MemberMiniList";
import RevenueOverview from "../RevenueOverview";
import TrendChart from "../TrendChart";
import { Button, Callout, LinkButton, PageStack, TwoCol } from "../ui";
import { PageProps } from "./types";
import {
  conversionTrend,
  expiringSoon,
  growthSeries,
  upgradeOpportunities,
} from "../../../utils/partnerMetrics";

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
  gap: 20px;
  align-items: start;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;
`;

interface OverviewPageProps extends PageProps {
  showProfileCardPrompt: boolean;
  onCreateProfileCard: () => void;
}

const OverviewPage = (props: OverviewPageProps) => {
  const {
    members,
    products,
    capabilities,
    loading,
    projection,
    projectionLoading,
    onNavigate,
    showProfileCardPrompt,
    onCreateProfileCard,
  } = props;

  const expiring = useMemo(() => expiringSoon(members), [members]);
  const opportunities = useMemo(() => upgradeOpportunities(members), [members]);
  const growth = useMemo(() => growthSeries(members), [members]);
  const conversion = useMemo(() => conversionTrend(members), [members]);

  return (
    <PageStack>
      {showProfileCardPrompt && (
        <Callout>
          Your studio's profile card isn't set up yet. Members see it when they browse studios.
          <Button $variant="primary" onClick={onCreateProfileCard}>
            Create profile card
          </Button>
        </Callout>
      )}
      <KpiStrip
        members={members}
        products={products}
        capabilities={capabilities}
        projection={projection}
        projectionLoading={projectionLoading}
        loading={loading}
      />
      <OverviewGrid>
        <Column>
          <MemberFunnel members={members} loading={loading} />
          <TwoCol>
            <TrendChart
              title="Member Growth"
              data={growth}
              loading={loading}
              color="#1f6f4a"
              caption="total members"
            />
            <TrendChart
              title="Trial → Paid Conversion"
              data={conversion}
              loading={loading}
              color="#8a4b9c"
              valueSuffix="%"
              caption="of trial members upgraded"
            />
          </TwoCol>
        </Column>
        <Column>
          <MemberMiniList
            title="Expiring Soon"
            members={expiring}
            loading={loading}
            emptyMessage="No memberships expiring in the next 7 days."
            action={<LinkButton onClick={() => onNavigate("expiring")}>View all</LinkButton>}
          />
          <MemberMiniList
            title="Upgrade Opportunities"
            members={opportunities}
            loading={loading}
            emptyMessage="No trial members are currently active."
            action={<LinkButton onClick={() => onNavigate("opportunities")}>View all</LinkButton>}
          />
          {capabilities.canViewRevenue && (
            <RevenueOverview members={members} products={products} loading={loading} />
          )}
        </Column>
      </OverviewGrid>
    </PageStack>
  );
};

export default OverviewPage;
